import { Injectable } from '@angular/core';

import type { Observable } from 'rxjs';

import { injectReportsHttpApiService } from '@core/reports-contracts';
import type {
  ReportDashboardComponent,
  ReportDashboardComponentCreate,
  ReportDashboardComponentCreateRequest,
  ReportDashboardComponentGridPosition,
  ReportsComponentHttpClientService,
} from '@core/reports-types';

const dropComponentLocalPropsBeforeSave = (
  component: ReportDashboardComponentCreate | ReportDashboardComponent,
) => {
  if ('id' in component) {
    const { id, ...result } = component;
    return result;
  }

  return component;
};

const sanitizeGrid = (
  grid: ReportDashboardComponentGridPosition,
): ReportDashboardComponentGridPosition => ({
  x: grid.x,
  y: grid.y,
  cols: grid.cols,
  rows: grid.rows,
});

@Injectable()
export class ReportsComponentsHttpClient implements ReportsComponentHttpClientService {
  private readonly http = injectReportsHttpApiService();

  public createOne(
    dashboardId: number,
    component: ReportDashboardComponentCreateRequest,
  ): Observable<{ component: ReportDashboardComponent }> {
    return this.http.post(`/dashboards/${dashboardId}/components`, null, {
      component: dropComponentLocalPropsBeforeSave(component),
    });
  }

  public updateOne(
    dashboardId: number,
    component: ReportDashboardComponent,
  ): Observable<{ component: ReportDashboardComponent }> {
    return this.http.patch(`/dashboards/${dashboardId}/components/${component.id}`, null, {
      component: { ...component, grid: sanitizeGrid(component.grid) },
    });
  }

  public updateGridProps(
    dashboardId: number,
    components: Array<Pick<ReportDashboardComponent, 'id' | 'grid'>>,
  ): Observable<{ components: ReportDashboardComponent[] }> {
    return this.http.patch<{ components: ReportDashboardComponent[] }>(
      `/dashboards/${dashboardId}/update_positions`,
      null,
      { components },
    );
  }

  public removeOne(
    dashboardId: number,
    componentId: number | string,
  ): Observable<{ component: ReportDashboardComponent }> {
    return this.http.delete(`/dashboards/${dashboardId}/components/${componentId}`);
  }
}
